import type { ReactElement } from "react";
import { Link, useSearchParams } from "react-router";

import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { MessagePage } from "../../components/message-page.js";
import { FormSummary, TextField } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";
import { forgotPasswordRules, newPasswordRules } from "../../forms/view-model-rules.js";

import { ConfirmEmailFailedPage } from "./confirm-email-pages.js";
import { accountApi, accountUrls } from "./urls.js";

/**
 * パスワードを忘れたとき（再設定メールを送る → リンクから新しいパスワードを決める）。
 */

export function ForgotPasswordPage(): ReactElement {
    usePageMeta("パスワードをお忘れの方");
    const form = useApiForm(accountApi.forgotPassword, forgotPasswordRules);

    return (
        <div className="page max-w-md">
            <p className="mb-4"><Link to={accountUrls.login} className="btn-secondary btn-sm">ログイン画面へ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title">パスワードをお忘れの方</h1>
                <p className="page-lead">ご登録のメールアドレスへ、再設定用のリンクをお送りします。</p>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />

                    <TextField
                        label="メールアドレス" name="Email" type="email" required
                        placeholder="mail@example.com" autoFocus
                        wrapperClassName="mb-7"
                        error={form.fieldError("Email")}
                    />

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>再設定メールを送る</button>
                </form>
            </div>
        </div>
    );
}

/** <b>アカウントの有無を出さない</b>（どのアドレスでも同じ文。推測させない）。 */
export function ForgotPasswordConfirmationPage(): ReactElement {
    return (
        <MessagePage
            title="再設定メールをお送りしました"
            actions={<Link to={accountUrls.login} className="btn-secondary">ログイン画面へ</Link>}
        >
            ご登録のアカウントが存在する場合、再設定用のリンクをお送りしています。<br />
            メール内のリンクからパスワードを再設定してください。
        </MessagePage>
    );
}

/** 再設定メールのリンク（<c>?code=</c>）。<b>コードが無ければ入力させない</b>（有効化できなかったときと同じ画面を出す）。 */
export function ResetPasswordPage(): ReactElement {
    const [search] = useSearchParams();
    const code = queryValue(search, "code") ?? "";

    return code === "" ? <ConfirmEmailFailedPage /> : <ResetPasswordForm code={code} />;
}

function ResetPasswordForm({ code }: { readonly code: string }): ReactElement {
    usePageMeta("パスワードの再設定");
    const form = useApiForm(accountApi.resetPassword, newPasswordRules);

    return (
        <div className="page max-w-md">
            <div className="panel">
                <h1 className="page-title">パスワードの再設定</h1>
                <p className="page-lead">新しいパスワードを設定してください。</p>

                <form method="post" noValidate onSubmit={form.onSubmit} onChange={form.onChange} onBlur={form.onBlur}>
                    <FormSummary messages={form.summary} />
                    <input type="hidden" name="Code" value={code} />

                    <TextField
                        label="メールアドレス" name="Email" type="email" required
                        placeholder="mail@example.com" autoComplete="username" autoFocus
                        error={form.fieldError("Email")}
                    />
                    <TextField
                        label="パスワード" name="Password" type="password" required
                        placeholder="••••••••••" autoComplete="new-password"
                        help="10 文字以上で入力してください。"
                        error={form.fieldError("Password")}
                    />
                    <TextField
                        label="パスワードの確認入力" name="ConfirmPassword" type="password"
                        placeholder="••••••••••" autoComplete="new-password"
                        wrapperClassName="mb-7"
                        error={form.fieldError("ConfirmPassword")}
                    />

                    <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>パスワードを再設定する</button>
                </form>
            </div>
        </div>
    );
}

export function ResetPasswordConfirmationPage(): ReactElement {
    return (
        <MessagePage
            title="パスワードを再設定しました"
            actions={<Link to={accountUrls.login} className="btn-primary">ログイン画面へ</Link>}
        >
            新しいパスワードでログインしてください。
        </MessagePage>
    );
}
